import type { Doc } from "building-types/doc";
import hashString from "common/utils/hashString";
import { withSeededUids } from "common/utils/uid";
import { allDocMigrations } from "./docMigrations";
import { schemaVersionCompare, getNextSchemaVersion } from "./docMigrationVersion";

export const CURRENT_SCHEMA_VERSION = getNextSchemaVersion(allDocMigrations);

export function docMigrate(
  json: any,
  maxSchemaVersion: string = CURRENT_SCHEMA_VERSION,
  warnings: string[] = [],
  errors: string[] = [],
): Doc {
  if (typeof json.schemaVersion === "undefined") {
    json.schemaVersion = "0.0.1";
    delete json.fileVersion;
  }

  if (typeof json.schemaVerson !== "undefined") {
    json.schemaVersion = json.schemaVerson;
    delete json.schemaVerson;
  }

  if (schemaVersionCompare(json.schemaVersion, ">", CURRENT_SCHEMA_VERSION)) {
    throw new Error(
      `Cannot migrate future schema version \`${json.schemaVersion}\` to \`${CURRENT_SCHEMA_VERSION}\`. Please refresh your browser.`,
    );
  }

  // Docs below the current version are re-migrated on every read, and many migrations mint uid()s.
  // Seeding from the doc's own content keeps a parse reproducible.
  return withSeededUids(docMigrationSeed(json), () => applyDocMigrations(json, maxSchemaVersion, warnings, errors));
}

function applyDocMigrations(json: any, maxSchemaVersion: string, warnings: string[], errors: string[]): Doc {
  // defaultMigration (index 0) always applies — it unmarshals typed arrays and has
  // maxSchemaVersion 999.999.999. Early-exit must still run it before returning.
  if (schemaVersionCompare(json.schemaVersion, ">=", maxSchemaVersion)) {
    json = allDocMigrations[0].apply(json, warnings, errors);
    json.schemaVersion = maxSchemaVersion;
    return json;
  }

  for (const migration of allDocMigrations) {
    if (schemaVersionCompare(json.schemaVersion, "<=", migration.maxSchemaVersion)) {
      if (
        migration !== allDocMigrations[0] &&
        schemaVersionCompare(migration.maxSchemaVersion, ">=", maxSchemaVersion)
      ) {
        continue;
      }

      json = migration.apply(json, warnings, errors);
    }
  }

  json.schemaVersion = maxSchemaVersion;

  return json;
}

/**
 * Per-doc rather than one fixed seed: minted ids are referenced from rows outside the doc
 * (builder-scoped option set instances, option templates, procure measures), which a shared id
 * stream would let cross-talk. Stringified so a version and name that split differently
 * ("0.0.1" + "23" against "0.0.12" + "3") cannot land on the same seed.
 */
function docMigrationSeed(json: any): number {
  return hashString(
    JSON.stringify([
      json.schemaVersion,
      json.name ?? "",
      Object.keys(json.blocks ?? {}),
      Object.keys(json.levels ?? {}),
      Object.keys(json.elevations ?? {}),
    ]),
  );
}
